import { useCallback, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, RotateCcw, Trophy, Zap, Timer } from "lucide-react";

const GAME_DURATION = 20; // seconds
const TARGETS_START_RADIUS = 32;
const TARGETS_MIN_RADIUS = 18;

// Random position within play area with margin
const randomPos = (rect, radius) => {
  const pad = radius + 8;
  return {
    x: pad + Math.random() * Math.max(1, rect.width - pad * 2),
    y: pad + Math.random() * Math.max(1, rect.height - pad * 2),
  };
};

const Playground = () => {
  const areaRef = useRef(null);
  const [status, setStatus] = useState("idle"); // idle | playing | done
  const [score, setScore] = useState(0);
  const [best, setBest] = useState(0);
  const [timeLeft, setTimeLeft] = useState(GAME_DURATION);
  const [target, setTarget] = useState({ x: 100, y: 100, r: TARGETS_START_RADIUS });
  const [ripples, setRipples] = useState([]);

  useEffect(() => {
    const stored = Number(localStorage.getItem("nn-reflex-best") || 0);
    setBest(stored);
  }, []);

  // Timer
  useEffect(() => {
    if (status !== "playing") return;
    if (timeLeft <= 0) {
      setStatus("done");
      if (score > best) {
        setBest(score);
        localStorage.setItem("nn-reflex-best", String(score));
      }
      return;
    }
    const id = setTimeout(() => setTimeLeft((t) => t - 1), 1000);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, timeLeft]);

  const spawn = useCallback((currentScore = score) => {
    const rect = areaRef.current?.getBoundingClientRect();
    if (!rect) return;
    const r = Math.max(TARGETS_MIN_RADIUS, TARGETS_START_RADIUS - currentScore * 0.6);
    const p = randomPos({ width: rect.width, height: rect.height }, r);
    setTarget({ ...p, r });
  }, [score]);

  const start = () => {
    setScore(0);
    setTimeLeft(GAME_DURATION);
    setStatus("playing");
    setRipples([]);
    setTimeout(() => spawn(0), 60);
  };

  const reset = () => {
    setStatus("idle");
    setScore(0);
    setTimeLeft(GAME_DURATION);
  };

  const hit = (e) => {
    e.stopPropagation();
    if (status !== "playing") return;
    const rect = areaRef.current?.getBoundingClientRect();
    if (!rect) return;
    const rx = e.clientX - rect.left;
    const ry = e.clientY - rect.top;
    const id = Math.random().toString(36).slice(2);
    setRipples((r) => [...r, { id, x: rx, y: ry }]);
    setTimeout(() => setRipples((r) => r.filter((rp) => rp.id !== id)), 600);
    setScore((s) => {
      const next = s + 1;
      spawn(next);
      return next;
    });
  };

  const missShake = useRef(0);
  const onAreaClick = (e) => {
    if (status !== "playing") return;
    // If clicked on empty area (not the target), tiny visual feedback
    missShake.current += 1;
    const el = areaRef.current;
    if (!el) return;
    el.animate(
      [
        { transform: "translateX(0)" },
        { transform: "translateX(-4px)" },
        { transform: "translateX(4px)" },
        { transform: "translateX(0)" },
      ],
      { duration: 200, easing: "cubic-bezier(0.22,1,0.36,1)" }
    );
  };

  return (
    <section id="playground" className="py-24 md:py-36 relative overflow-hidden" data-testid="playground">
      <div className="mx-auto max-w-[1600px] px-5 md:px-10">
        {/* Header */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 mb-10 md:mb-14 items-end">
          <div className="md:col-span-7">
            <div className="section-label mb-5">◆ Playground · 006</div>
            <h2 className="h-display text-5xl md:text-7xl lg:text-[7rem] tracking-tighter leading-[0.88]">
              Now, <span className="font-serif-i text-[color:var(--nn-muted)]">play</span> with me.
            </h2>
          </div>
          <p className="md:col-span-5 text-[color:var(--nn-text)]/80 text-sm md:text-base leading-relaxed">
            A tiny reflex arcade — because every good portfolio deserves at least one moment of joy.
            Hit the lime dot as many times as you can in 20 seconds. Targets shrink as you get faster.
          </p>
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-3 gap-3 md:gap-6 mb-6 md:mb-8">
          <div className="p-4 md:p-5 rounded-2xl border border-[color:var(--nn-border)] bg-[color:var(--nn-surface)]/50 backdrop-blur">
            <div className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--nn-muted)] mb-2 flex items-center gap-2">
              <Zap size={12} /> Score
            </div>
            <div className="h-display text-3xl md:text-5xl tabular-nums" data-testid="playground-score">
              {String(score).padStart(2, "0")}
            </div>
          </div>
          <div className="p-4 md:p-5 rounded-2xl border border-[color:var(--nn-border)] bg-[color:var(--nn-surface)]/50 backdrop-blur">
            <div className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--nn-muted)] mb-2 flex items-center gap-2">
              <Timer size={12} /> Time
            </div>
            <div className="h-display text-3xl md:text-5xl tabular-nums" data-testid="playground-time">
              {String(timeLeft).padStart(2, "0")}s
            </div>
          </div>
          <div className="p-4 md:p-5 rounded-2xl border border-[color:var(--nn-accent)] bg-[color:var(--nn-accent)]/10 backdrop-blur">
            <div className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--nn-accent-text)] mb-2 flex items-center gap-2">
              <Trophy size={12} /> Best
            </div>
            <div className="h-display text-3xl md:text-5xl text-[color:var(--nn-accent-text)] tabular-nums" data-testid="playground-best">
              {String(best).padStart(2, "0")}
            </div>
          </div>
        </div>

        {/* Play area */}
        <div
          ref={areaRef}
          onClick={onAreaClick}
          className="relative h-[420px] md:h-[520px] rounded-3xl border border-[color:var(--nn-border)] overflow-hidden bg-[color:var(--nn-surface)]/40 backdrop-blur"
          data-testid="playground-area"
        >
          {/* Grid backdrop */}
          <div
            aria-hidden
            className="absolute inset-0 opacity-30"
            style={{
              backgroundImage:
                "linear-gradient(to right, var(--nn-border) 1px, transparent 1px), linear-gradient(to bottom, var(--nn-border) 1px, transparent 1px)",
              backgroundSize: "48px 48px",
            }}
          />

          {/* Corner marks */}
          {["top-4 left-4", "top-4 right-4", "bottom-4 left-4", "bottom-4 right-4"].map((pos, i) => (
            <div key={i} className={`absolute ${pos} font-mono text-[10px] uppercase tracking-widest text-[color:var(--nn-muted)]`}>
              {i === 0 && "▚ arena"}
              {i === 1 && "sector 07"}
              {i === 2 && "reflex.exe"}
              {i === 3 && "v.1.0"}
            </div>
          ))}

          {/* Ripples */}
          <AnimatePresence>
            {ripples.map((r) => (
              <motion.div
                key={r.id}
                initial={{ scale: 0, opacity: 0.8 }}
                animate={{ scale: 3, opacity: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                className="absolute w-16 h-16 rounded-full border-2 border-[color:var(--nn-accent)] pointer-events-none"
                style={{ left: r.x - 32, top: r.y - 32 }}
              />
            ))}
          </AnimatePresence>

          {/* Idle overlay */}
          {status === "idle" && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="absolute inset-0 grid place-items-center text-center px-6"
            >
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--nn-muted)] mb-3">
                  ▚ Nirav's Reflex Arena
                </div>
                <div className="h-display text-3xl md:text-5xl mb-6">
                  Ready to <span className="font-serif-i text-[color:var(--nn-muted)]">play?</span>
                </div>
                <button
                  onClick={start}
                  data-cursor="dragging"
                  data-cursor-label="play"
                  data-testid="playground-start"
                  className="group inline-flex items-center gap-3 pl-6 pr-2 py-2 rounded-full bg-[color:var(--nn-accent)] text-[color:var(--nn-accent-ink)] font-medium hover:gap-4 transition-all"
                >
                  Start reflex test
                  <span className="w-10 h-10 rounded-full grid place-items-center bg-[color:var(--nn-accent-ink)] text-[color:var(--nn-accent)]">
                    <Play size={16} className="ml-0.5" />
                  </span>
                </button>
              </div>
            </motion.div>
          )}

          {/* Target */}
          {status === "playing" && (
            <motion.button
              key={`${target.x}-${target.y}-${score}`}
              onClick={hit}
              initial={{ scale: 0, rotate: -30 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ type: "spring", stiffness: 340, damping: 18 }}
              className="absolute rounded-full bg-[color:var(--nn-accent)] shadow-[0_0_40px_rgba(223,255,28,0.4)] cursor-pointer"
              style={{
                left: target.x - target.r,
                top: target.y - target.r,
                width: target.r * 2,
                height: target.r * 2,
              }}
              data-cursor="dragging"
              data-cursor-label="hit"
              data-testid="playground-target"
              aria-label="Target — click to score"
            >
              <span className="absolute inset-2 rounded-full border-2 border-[color:var(--nn-accent-ink)]/40" />
              <span className="absolute inset-[35%] rounded-full bg-[color:var(--nn-accent-ink)]" />
            </motion.button>
          )}

          {/* Done overlay */}
          {status === "done" && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="absolute inset-0 grid place-items-center text-center px-6 bg-[color:var(--nn-bg)]/85 backdrop-blur"
            >
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--nn-accent-text)] mb-3">
                  ▚ Round complete
                </div>
                <div className="h-display text-5xl md:text-7xl mb-2">
                  {score} <span className="font-serif-i text-[color:var(--nn-muted)] text-4xl">hits</span>
                </div>
                <div className="text-sm text-[color:var(--nn-text)]/80 mb-6">
                  {score >= best && score > 0
                    ? "New personal best — nice reflexes."
                    : best > 0 ? `Best: ${best}. Beat it?` : "Fun start. Try again?"}
                </div>
                <div className="flex items-center justify-center gap-3">
                  <button
                    onClick={start}
                    data-cursor="dragging"
                    data-cursor-label="again"
                    data-testid="playground-again"
                    className="group inline-flex items-center gap-3 pl-6 pr-2 py-2 rounded-full bg-[color:var(--nn-accent)] text-[color:var(--nn-accent-ink)] font-medium hover:gap-4 transition-all"
                  >
                    Play again
                    <span className="w-10 h-10 rounded-full grid place-items-center bg-[color:var(--nn-accent-ink)] text-[color:var(--nn-accent)]">
                      <Play size={16} className="ml-0.5" />
                    </span>
                  </button>
                  <button
                    onClick={reset}
                    data-cursor="hover"
                    data-testid="playground-reset"
                    className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-[color:var(--nn-border)] hover:border-[color:var(--nn-accent)] transition-colors"
                  >
                    <RotateCcw size={14} /> Reset
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </div>

        {/* Footnote */}
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 font-mono text-[10px] uppercase tracking-widest text-[color:var(--nn-muted)]">
          <span>▚ Built with framer-motion · No frameworks harmed</span>
          <span>Personal best saved locally · Zero telemetry</span>
        </div>
      </div>
    </section>
  );
};

export default Playground;
